import { useEffect, useState } from "react";
import type {
  DfyBuildPositionDto,
  DfyBuildTimingsDto,
  DfyEtaDto,
  Locale
} from "../shared/dto";
import { dfyStepLabel, type DfyRunStep } from "../shared/dfy-catalog";
import { dfyRunTimeline, dfyStageLabelKey, type DfyRunStage } from "../shared/dfy-stages";
import { dfyEtaRangeMinutes, dfyReadyAtLabel } from "./DfyEtaLine";
import { WorkingAvatar } from "./WorkingAvatar";
import "./build-progress.css";

/**
 * The ONE way the interface shows a build being made (Pavel 2026-08-05:
 * "kde říkáš hotových kroků, potřebuji vyjet kolik z kolika... ty etapy
 * propracovat, aby to nebylo takový nudný na to koukat a čekat. A nevím, jak
 * dlouho mám čekat vůbec").
 *
 * Before this, a partner watching a live build in the queue saw nothing at all,
 * the review card said "hotových kroků: 6" without a total, and the client got
 * one sentence. Three surfaces, three different amounts of truth about the same
 * run.
 *
 * What it shows, in the order it answers the questions a waiting person asks:
 *  1. WHERE ARE WE - "Krok 6 z 15" plus the step's human name, from
 *     `dfyRunProgress`, the same derivation the estimator measures against.
 *  2. WHAT HAS IT COST - each finished ETAPA with the time the SERVER measured
 *     for it, so the wait has a shape instead of being one long blank.
 *  3. WHAT IS HAPPENING NOW - the current stage opened up to its steps, the
 *     pulsing avatar on the step in progress, and how long it has been on it.
 *  4. HOW MUCH LONGER - the honest band when history can carry one, and when it
 *     cannot, WHY: "this is only the second build of this material".
 *
 * The estimate laws (F13) are untouched: this component never computes a
 * remaining time. It renders `eta` when the server sent one and says so plainly
 * when it did not. A per-step median is a per-step statement and is never
 * summed into a finish time.
 */

export type BuildProgressDensity = "stage" | "panel";

export interface BuildProgressProps {
  gateKey: string;
  steps: readonly DfyRunStep[];
  startedAt: string;
  stepManifest?: readonly string[] | null;
  timings?: DfyBuildTimingsDto | null;
  eta?: DfyEtaDto | null;
  locale: Locale;
  t: (key: string) => string;
  /** `stage` is the client's whole waiting screen, `panel` sits inside a card. */
  density?: BuildProgressDensity;
  profilePhotoUrl?: string | null;
  /** The partner also gets the clock time; the client only needs the band. */
  showReadyAt?: boolean;
  timeZone?: string;
  /** Frozen clock for tests and server rendering. */
  now?: number;
  /** False when the caller already wraps this in a live region. */
  live?: boolean;
}

/** How often the elapsed counters catch up. Minute resolution needs nothing
 * faster, and a slow tick keeps a waiting screen from being a busy one. */
const TICK_MS = 30_000;

/**
 * "3 min", "48 s", "1 h 20 min".
 *
 * Seconds matter below a minute: half the prep steps finish in twenty seconds
 * and rounding them to "0 min" reads as broken rather than fast.
 */
export function formatBuildDuration(ms: number, t: (key: string) => string): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return t("dfyEta.durationSeconds").replace("{seconds}", String(seconds));
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return t("dfyEta.durationMinutes").replace("{minutes}", String(minutes));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0
    ? t("dfyEta.durationHours").replace("{hours}", String(hours))
    : t("dfyEta.durationHoursMinutes")
      .replace("{hours}", String(hours))
      .replace("{minutes}", String(rest));
}

/** Czech counts a noun three ways (1 krok / 2-4 kroky / 5+ kroků), so a single
 * "{count} kroků" template would print "1 kroků" on every one-step stage. */
export function stageStepsLabel(count: number, t: (key: string) => string): string {
  const key = count === 1
    ? "dfyEta.stageStepsOne"
    : count >= 2 && count <= 4
      ? "dfyEta.stageStepsFew"
      : "dfyEta.stageStepsMany";
  return t(key).replace("{count}", String(count));
}

/** Re-render on a slow tick so elapsed times move while nothing else changes.
 * Effects do not run under `renderToStaticMarkup`, so a frozen `now` prop stays
 * authoritative in tests and on the server. */
function useClock(frozen?: number): number {
  const [tick, setTick] = useState(() => frozen ?? Date.now());
  useEffect(() => {
    if (frozen !== undefined) return;
    const timer = setInterval(() => setTick(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [frozen]);
  return frozen ?? tick;
}

function elapsedSince(iso: string, now: number): number | null {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, now - parsed);
}

export function BuildProgress(props: BuildProgressProps) {
  const now = useClock(props.now);
  const density = props.density ?? "panel";
  const timeline = dfyRunTimeline({
    gateKey: props.gateKey,
    steps: props.steps,
    stepManifest: props.stepManifest ?? null,
    observed: props.timings?.observed,
    typical: props.timings?.typical
  });
  const { progress, stages } = timeline;
  const stepLabel = progress.currentStepKey
    ? dfyStepLabel(progress.currentStepKey, props.t)
    : props.t("dfyEta.finishing");
  const percent = progress.totalSteps > 0
    ? Math.round((progress.completedSteps / progress.totalSteps) * 100)
    : 0;
  const headline = progress.totalSteps > 0
    ? props.t("dfyEta.step")
      .replace("{current}", String(progress.currentStepNumber))
      .replace("{total}", String(progress.totalSteps))
      .replace("{label}", stepLabel)
    : props.t("dfyEta.finishing");
  const liveProps = props.live === false
    ? {}
    : { role: "status", "aria-live": "polite" as const };

  return (
    <section
      className={`build-progress is-${density}`}
      data-build-progress={density}
      data-build-gate={props.gateKey}
      data-build-step={progress.currentStepNumber}
      data-build-total={progress.totalSteps}
    >
      <header className="build-progress-head">
        <p className="build-progress-count" {...liveProps}>{headline}</p>
        {progress.totalSteps > 0 && (
          <div
            className="build-progress-bar"
            role="img"
            aria-label={props.t("dfyEta.doneOf")
              .replace("{done}", String(progress.completedSteps))
              .replace("{total}", String(progress.totalSteps))}
          >
            <span className="build-progress-bar-fill" style={{ width: `${percent}%` }} />
          </div>
        )}
      </header>

      {stages.length > 0 && (
        <ol className="build-progress-stages">
          {stages.map((stage) => (
            <BuildStageRow
              key={stage.id}
              stage={stage}
              now={now}
              currentStepSince={props.timings?.currentStepSince ?? props.startedAt}
              profilePhotoUrl={props.profilePhotoUrl ?? null}
              t={props.t}
            />
          ))}
        </ol>
      )}

      <BuildProgressFooter
        eta={props.eta ?? null}
        timings={props.timings ?? null}
        startedAt={props.startedAt}
        now={now}
        locale={props.locale}
        showReadyAt={props.showReadyAt}
        timeZone={props.timeZone}
        t={props.t}
      />
    </section>
  );
}

/**
 * One ETAPA. Finished stages state what they cost, the running one opens up to
 * its steps, and the ones ahead say what they usually take - so the eye has
 * somewhere to go while the build works.
 */
function BuildStageRow(props: {
  stage: DfyRunStage;
  now: number;
  currentStepSince: string;
  profilePhotoUrl: string | null;
  t: (key: string) => string;
}) {
  const { stage, t } = props;
  const onStepMs = stage.state === "current" ? elapsedSince(props.currentStepSince, props.now) : null;
  const note = stage.state === "done" && stage.durationMs !== null
    ? formatBuildDuration(stage.durationMs, t)
    : stage.state === "current" && onStepMs !== null
      ? t("dfyEta.runningFor").replace("{duration}", formatBuildDuration(onStepMs, t))
      : stage.state === "upcoming" && stage.typicalMs !== null
        ? t("dfyEta.usually").replace("{duration}", formatBuildDuration(stage.typicalMs, t))
        : null;
  return (
    <li className={`build-progress-stage is-${stage.state}`} data-stage-state={stage.state}>
      <div className="build-progress-stage-head">
        <span className="build-progress-mark" aria-hidden="true">
          {stage.state === "current"
            ? <WorkingAvatar size="card" profilePhotoUrl={props.profilePhotoUrl} />
            : stage.state === "done" ? "✓" : stage.state === "skipped" ? "·" : "○"}
        </span>
        <span className="build-progress-stage-name">{t(dfyStageLabelKey(stage.stepClass))}</span>
        <span className="build-progress-stage-count">{stageStepsLabel(stage.steps.length, t)}</span>
        {note && <span className="build-progress-stage-time">{note}</span>}
      </div>
      {stage.state === "current" && (
        <ol className="build-progress-steps">
          {stage.steps.map((step) => {
            const stepNote = step.state === "done" && step.durationMs !== null
              ? formatBuildDuration(step.durationMs, t)
              : step.state === "current" && onStepMs !== null
                ? t("dfyEta.onStep").replace("{duration}", formatBuildDuration(onStepMs, t))
                : step.state === "upcoming" && step.typicalMs !== null
                  ? t("dfyEta.usually").replace("{duration}", formatBuildDuration(step.typicalMs, t))
                  : null;
            return (
              <li className={`is-${step.state}`} key={step.key} data-step-state={step.state}>
                <span className="build-progress-step-mark" aria-hidden="true">
                  {step.state === "done"
                    ? "✓"
                    : step.state === "current" ? "●" : step.state === "skipped" ? "·" : "○"}
                </span>
                <span className="build-progress-step-name">{dfyStepLabel(step.key, t)}</span>
                {step.state === "skipped"
                  ? <span className="build-progress-step-time">{t("dfyEta.skipped")}</span>
                  : stepNote && <span className="build-progress-step-time">{stepNote}</span>}
              </li>
            );
          })}
        </ol>
      )}
    </li>
  );
}

/**
 * The answer to "how long am I waiting".
 *
 * With enough history that is the honest P25-P75 band. Without it the honest
 * answer is the reason: a young gate has nothing to average yet. Saying which
 * build number this is beats a bare "the estimate is learning", which reads
 * like a defect rather than a fact about a system that has not run often.
 */
function BuildProgressFooter(props: {
  eta: DfyEtaDto | null;
  timings: DfyBuildTimingsDto | null;
  startedAt: string;
  now: number;
  locale: Locale;
  showReadyAt?: boolean;
  timeZone?: string;
  t: (key: string) => string;
}) {
  const { t } = props;
  const runningMs = elapsedSince(props.startedAt, props.now);
  const range = props.eta ? dfyEtaRangeMinutes(props.eta) : null;
  const readyAt = props.eta && props.showReadyAt
    ? dfyReadyAtLabel(props.eta.etaAt, props.locale, props.timeZone)
    : null;
  const runNumber = props.timings ? props.timings.historyRuns + 1 : null;
  return (
    <div className="build-progress-foot" data-dfy-eta-source={props.eta?.source ?? "none"}>
      {range ? (
        <p className="build-progress-remaining">
          {t("dfyEta.remainingRange")
            .replace("{from}", String(range.from))
            .replace("{to}", String(range.to))}
        </p>
      ) : (
        <p className="build-progress-remaining is-learning">
          {runNumber === null
            ? t("dfyEta.learning")
            : runNumber === 1
              ? t("dfyEta.learningFirst")
              : t("dfyEta.learningRun").replace("{number}", String(runNumber))}
        </p>
      )}
      {readyAt && (
        <p className="build-progress-ready">{t("dfyEta.readyAt").replace("{time}", readyAt)}</p>
      )}
      {runningMs !== null && (
        <p className="build-progress-elapsed">
          {t("dfyEta.runningTotal").replace("{duration}", formatBuildDuration(runningMs, t))}
          {props.eta ? ` · ${t("dfyEta.sample").replace("{count}", String(props.eta.sampleSize))}` : ""}
        </p>
      )}
    </div>
  );
}

/**
 * The queue's one-liner: same counter, same step name, same clock, in the space
 * a list row has. The queue used to carry only `eta`, which during cold start
 * is nothing, so a partner scrolling past a live build could not tell it from
 * an idle row.
 */
export function BuildProgressRow(props: {
  position: DfyBuildPositionDto;
  t: (key: string) => string;
  now?: number;
}) {
  const now = useClock(props.now);
  const { position, t } = props;
  const onStepMs = elapsedSince(position.currentStepSince, now);
  const percent = position.totalSteps > 0
    ? Math.round((position.completedSteps / position.totalSteps) * 100)
    : 0;
  const label = position.currentStepKey ? dfyStepLabel(position.currentStepKey, t) : t("dfyEta.finishing");
  return (
    <span className="build-progress-row" data-build-progress="row" data-build-step={position.currentStepNumber}>
      <span className="build-progress-row-bar" aria-hidden="true">
        <span className="build-progress-row-fill" style={{ width: `${percent}%` }} />
      </span>
      <span className="build-progress-row-text">
        {t("dfyEta.step")
          .replace("{current}", String(position.currentStepNumber))
          .replace("{total}", String(position.totalSteps))
          .replace("{label}", label)}
        {onStepMs !== null && ` · ${t("dfyEta.onStep").replace("{duration}", formatBuildDuration(onStepMs, t))}`}
      </span>
    </span>
  );
}
